import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const ProjectCard = ({ project, featured = false }) => {
  const location = useLocation();

  const handleCardClick = () => {
    sessionStorage.setItem(`scroll-${location.pathname}`, window.scrollY.toString());
  };

  return (
    <Link 
      to={`/projects/${project.id}`} 
      onClick={handleCardClick}
      title={`View Case Study: ${project.title}`}
      aria-label={`Open detailed technical analysis for the ${project.title} project`}
      className={`group relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl hover:shadow-accent/5 transition-smooth overflow-hidden ${
      featured ? 'col-span-1 md:col-span-2 lg:col-span-2' : 'col-span-1'
    }`}>
      {/* Project Image */}
      <div className={`relative overflow-hidden ${featured ? 'h-64 md:h-80' : 'h-48'}`}>
        <Image
          src={project.image}
          alt={project.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-smooth"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-smooth" />
        
        {/* Project Type Badge */}
        <div className="absolute top-4 left-4">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-accent text-white">
            {project.type}
          </span>
        </div>

        {/* View Case Study Button - Appears on Hover */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-smooth">
          <div
            className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 text-white px-6 py-3 rounded-lg font-heading font-semibold shadow-2xl transform hover:-translate-y-1 transition-smooth"
          >
            <span>View Case Study</span>
            <Icon name="ArrowRight" size={14} />
          </div>
        </div>
      </div>

      {/* Project Content */}
      <div className={`p-6 ${featured ? 'md:p-8' : ''}`}>
        <div className="flex items-start justify-between mb-3">
          <h3 className={`font-heading font-semibold text-text-primary group-hover:text-accent transition-smooth ${
            featured ? 'text-xl md:text-2xl' : 'text-lg'
          }`}>
            {project.title}
          </h3>
          {project.featured ? (
            <div className="flex items-center space-x-1 text-warning flex-shrink-0 ml-3">
              <Icon name="Star" size={16} className="fill-current" />
              <span className="text-xs font-medium">Featured</span>
            </div>
          ) : null}
        </div>

        <p className="text-text-secondary mb-4 text-sm leading-relaxed">
          {featured ? project.longDescription : project.description}
        </p>

        {/* Project Meta */}
        <div className="flex flex-wrap items-center gap-4 mb-4">
          {project.client && (
            <div className="flex items-center space-x-2 text-text-muted">
              <Icon name="Building" size={14} />
              <span className="text-xs">{project.client}</span>
            </div>
          )}
          {project.year && (
            <div className="flex items-center space-x-2 text-text-muted">
              <Icon name="Calendar" size={14} />
              <span className="text-xs">{project.year}</span>
            </div>
          )}
          {project.duration && (
            <div className="flex items-center space-x-2 text-text-muted">
              <Icon name="Clock" size={14} />
              <span className="text-xs">{project.duration}</span>
            </div>
          )}
        </div>

        {/* Services Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {project.services?.slice(0, featured ? 4 : 3).map((service, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-white/10 border border-white/5 text-text-secondary"
            >
              {service}
            </span>
          ))}
          {project.services?.length > (featured ? 4 : 3) && (
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-surface-200 text-text-muted">
              +{project.services.length - (featured ? 4 : 3)} more
            </span>
          )}
        </div>

        {/* Key Metric */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {project.metric && (
              <div className="flex items-center space-x-1">
                <Icon name="TrendingUp" size={16} className="text-success" />
                <span className="text-sm font-semibold text-success">{project.metric}</span>
              </div>
            )}
          </div>
          <div
            className="inline-flex items-center space-x-1 text-accent hover:text-accent-600 text-sm font-medium transition-smooth"
          >
            <span>Learn More</span>
            <Icon name="ArrowRight" size={14} />
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ProjectCard;